import {
    AuthenticationProvider,
    CookieConsentManager,
    DebugAdUnitsList,
    FeatureToggle,
    FlyoutNavStateProvider,
    IndexExchange,
    LiveRampATS,
    ScrollToTopOnNav,
    Sophi,
    SubscribeWithGoogle,
    TaboolaScriptProvider,
    TheWestLiveProvider,
    TheWestSection,
    UserAnalytics,
} from '@news-mono/component-library'
import {
    AdDefinition,
    AdEvent,
    AdState,
    BaseClientConfig,
    BreachScreenContextProvider,
    CookieConsentEvent,
    ExtendedAccessEvent,
    NavEvent,
    Product,
    RadioEvent,
    SubscribeEvent,
    SubscribeWithGoogleEvent,
    UserAuthEvents,
} from '@news-mono/web-common'
import H from 'history'
import React from 'react'
import { layout } from './App.routing'
import { BaseTheWest } from './BaseTheWest/BaseTheWest'
import { StyledSite } from './Site.styled'

export interface SiteProps {
    pageContents: React.ReactElement<any>
    pageType: string
    additionalPageProperties: { [key: string]: any }
    location: H.Location
    onEvent: (
        event:
            | NavEvent
            | AdEvent
            | UserAuthEvents
            | SubscribeEvent
            | CookieConsentEvent
            | ExtendedAccessEvent
            | SubscribeWithGoogleEvent
            | RadioEvent,
    ) => void
    config: BaseClientConfig
    ads: AdState | undefined
    section: TheWestSection
}

export interface State {
    mobileNavExpanded: boolean
}

const headerUnit: AdDefinition = {
    id: 'header',
    size: 'leaderboard768Above',
    isSitewideAd: true,
}

const stickyFooterUnit: AdDefinition = {
    id: 'sticky-footer',
    size: 'mobileLeaderboard',
}

const twlStickyPlayerUnit: AdDefinition = {
    id: 'twl-player',
    size: 'theWestLiveLogo',
}
const twlLandingPageUnit: AdDefinition = {
    id: 'twl-landing',
    size: 'theWestLiveLogo',
}
const twlBannerPageUnit: AdDefinition = {
    id: 'twl-banner',
    size: 'theWestLiveLogo',
}

export const siteAds: AdDefinition[] = [
    headerUnit,
    stickyFooterUnit,
    twlStickyPlayerUnit,
    twlLandingPageUnit,
    twlBannerPageUnit,
]

// This emulates the ads rendered above the content in this file for the ad-diagnostics page
export const headerCompositions = [
    layout.composition({
        type: 'thor',
        props: {},
        contentAreas: {
            main: [
                layout.component({
                    type: 'ad-unit',
                    props: {
                        noticePosition: 'top-right',
                        slot: headerUnit,
                    },
                }),
            ],
        },
    }),
]

export const footerCompositions = [
    layout.composition({
        type: 'thor',
        props: {},
        contentAreas: {
            main: [
                layout.component({
                    type: 'ad-unit',
                    props: {
                        noticePosition: 'below-center',
                        slot: stickyFooterUnit,
                    },
                }),
            ],
        },
    }),
]

export const Site: React.FC<SiteProps> = ({
    config,
    location,
    ads,
    section,
    onEvent,
    pageContents,
    additionalPageProperties,
    pageType,
}) => {
    const baseWest = (
        <BreachScreenContextProvider>
            <AuthenticationProvider>
                <TaboolaScriptProvider>
                    <SubscribeWithGoogle onEvent={onEvent} />
                    <FlyoutNavStateProvider>
                        {(flyOutProps) => (
                            <BaseTheWest
                                additionalPageProperties={
                                    additionalPageProperties
                                }
                                ads={ads}
                                config={config}
                                flyOutProps={flyOutProps}
                                location={location}
                                onEvent={onEvent}
                                pageContents={pageContents}
                                pageType={pageType}
                                product={Product.TheWest}
                                section={section}
                            />
                        )}
                    </FlyoutNavStateProvider>
                </TaboolaScriptProvider>
            </AuthenticationProvider>
        </BreachScreenContextProvider>
    )
    return (
        <React.Fragment>
            <UserAnalytics />
            <LiveRampATS />
            <IndexExchange section={section} product={Product.TheWest} />
            <Sophi />
            <StyledSite>
                <ScrollToTopOnNav />
                <FeatureToggle
                    feature="the-west-live"
                    on={() => (
                        <TheWestLiveProvider
                            stationId={config.theWestLiveStationId}
                            onEvent={onEvent}
                        >
                            {baseWest}
                        </TheWestLiveProvider>
                    )}
                    off={() => baseWest}
                />
                <FeatureToggle
                    feature="debug-ads"
                    on={() => (
                        <DebugAdUnitsList
                            ads={ads}
                            pathname={location.pathname}
                        />
                    )}
                />
            </StyledSite>
            <FeatureToggle
                feature="cookie-consent"
                on={() => <CookieConsentManager onEvent={onEvent} />}
            />
        </React.Fragment>
    )
}
Site.displayName = 'Site'
